import { useEffect, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import { useQuery } from '@tanstack/react-query';
import { useGameStore, type AnalysisArrow } from '@/store/gameStore';
import { useSettingsStore } from '@/store/settingsStore';
import { gameService } from '@/services/game.service';

/** Arrow colours per analysis line rank (best → worst). */
const LINE_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6'];

function formatScore(score: number, mate: number | null): string {
  if (mate !== null) {
    return mate > 0 ? `+M${mate}` : `-M${Math.abs(mate)}`;
  }
  const pawns = score / 100;
  if (pawns === 0) return '0.00';
  return (pawns > 0 ? '+' : '') + pawns.toFixed(2);
}

/** Simple debounce hook — returns the value only after `delay` ms of no changes. */
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export default function StudyPanel() {
  const {
    fen,
    fenStack,
    futureMoves,
    moves,
    localUndo,
    localRedo,
    jumpToMove,
    loadStudyFen,
    loadStudyPgn,
    setEvaluation,
    setAnalysisArrows,
    hintArrow,
    setHintArrow,
  } = useGameStore();

  const { analysisLines, setAnalysisLines } = useSettingsStore();

  // Load section
  const [loadTab, setLoadTab] = useState<'fen' | 'pgn'>('fen');
  const [fenInput, setFenInput] = useState('');
  const [pgnInput, setPgnInput] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  // Export feedback
  const [pgnCopied, setPgnCopied] = useState(false);
  const [fenCopied, setFenCopied] = useState(false);

  const canUndo = fenStack.length > 0;
  const canRedo = futureMoves.length > 0;

  // All moves visible (applied + future/redo) and cursor = how many are applied
  const allMoves = [...moves, ...futureMoves];
  const cursor   = moves.length;   // 0 = before first move, k = after k-th move

  // Ref for auto-scrolling the active move into view
  const activeMoveRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeMoveRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [cursor]);

  // Debounce FEN so rapid moves don't fire analysis on every intermediate position
  const debouncedFen = useDebounce(fen, 350);

  const analysisQuery = useQuery({
    queryKey: ['analysis', debouncedFen, analysisLines],
    queryFn: () => gameService.analyze(debouncedFen, analysisLines, false),
    staleTime: 60_000,
    gcTime: 5 * 60_000,   // keep cache entries 5 min before GC
    refetchOnWindowFocus: false,
    retry: 1,             // one retry max — engine errors rarely benefit from retrying
  });

  // Keep evaluation store + board arrows in sync with analysis results
  useEffect(() => {
    if (!analysisQuery.data) return;
    setEvaluation(analysisQuery.data.evaluation, analysisQuery.data.mate);

    const arrows: AnalysisArrow[] = analysisQuery.data.lines.map((line, i) => ({
      from: line.uci.slice(0, 2),
      to: line.uci.slice(2, 4),
      color: LINE_COLORS[i] ?? LINE_COLORS[LINE_COLORS.length - 1],
    }));
    setAnalysisArrows(arrows);
  }, [analysisQuery.data, setEvaluation, setAnalysisArrows]);

  const handleFenLoad = () => {
    const trimmed = fenInput.trim();
    if (!trimmed) return;
    try {
      new Chess(trimmed);
      loadStudyFen(trimmed);
      setFenInput('');
      setLoadError(null);
      if (hintArrow) setHintArrow(null);
    } catch {
      setLoadError('Invalid FEN string');
    }
  };

  const handlePgnLoad = () => {
    const trimmed = pgnInput.trim();
    if (!trimmed) return;
    try {
      loadStudyPgn(trimmed);
      setPgnInput('');
      setLoadError(null);
      if (hintArrow) setHintArrow(null);
    } catch {
      setLoadError('Invalid PGN — check move syntax');
    }
  };

  const handleLoadKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') loadTab === 'fen' ? handleFenLoad() : handlePgnLoad();
  };

  const buildPgn = () => {
    if (moves.length === 0) return '';
    const tokens: string[] = [];
    moves.forEach((m, i) => {
      if (i % 2 === 0) tokens.push(`${Math.floor(i / 2) + 1}.`);
      tokens.push(m.san);
    });
    return tokens.join(' ');
  };

  const handleCopyPgn = () => {
    const text = buildPgn();
    if (!text) return;
    navigator.clipboard.writeText(text).catch(() => {});
    setPgnCopied(true);
    setTimeout(() => setPgnCopied(false), 2000);
  };

  const handleCopyFen = () => {
    navigator.clipboard.writeText(fen).catch(() => {});
    setFenCopied(true);
    setTimeout(() => setFenCopied(false), 2000);
  };

  return (
    <div className="card p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">
          Study Mode
        </h3>
        <span className="text-[10px] bg-blue-900/40 text-blue-300 border border-blue-700/40 rounded px-2 py-0.5">
          FREE BOARD
        </span>
      </div>

      {/* Load section — FEN / PGN tabs */}
      <div>
        {/* Tab switcher */}
        <div className="flex mb-2 rounded overflow-hidden border border-gray-700 text-xs">
          <button
            type="button"
            onClick={() => { setLoadTab('fen'); setLoadError(null); }}
            className={`flex-1 py-1 font-medium transition-colors ${
              loadTab === 'fen'
                ? 'bg-blue-700 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            FEN
          </button>
          <button
            type="button"
            onClick={() => { setLoadTab('pgn'); setLoadError(null); }}
            className={`flex-1 py-1 font-medium transition-colors ${
              loadTab === 'pgn'
                ? 'bg-blue-700 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            PGN
          </button>
        </div>

        {loadTab === 'fen' ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={fenInput}
              onChange={(e) => { setFenInput(e.target.value); setLoadError(null); }}
              onKeyDown={handleLoadKeyDown}
              placeholder="Paste FEN…"
              className="flex-1 min-w-0 bg-gray-800 border border-gray-700 rounded px-2 py-1.5
                         text-xs text-white placeholder:text-gray-600
                         focus:outline-none focus:border-blue-500"
            />
            <button
              type="button"
              onClick={handleFenLoad}
              disabled={!fenInput.trim()}
              className="btn-secondary text-xs py-1.5 px-3 shrink-0 disabled:opacity-40"
            >
              Load
            </button>
          </div>
        ) : (
          <>
            <textarea
              value={pgnInput}
              onChange={(e) => { setPgnInput(e.target.value); setLoadError(null); }}
              onKeyDown={handleLoadKeyDown}
              placeholder={`Paste PGN…\ne.g. 1. e4 e5 2. Nf3 Nc6`}
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5
                         text-xs text-white placeholder:text-gray-600 font-mono resize-none
                         focus:outline-none focus:border-blue-500"
            />
            <button
              type="button"
              onClick={handlePgnLoad}
              disabled={!pgnInput.trim()}
              className="btn-secondary w-full text-xs py-1.5 mt-1.5 disabled:opacity-40"
            >
              Load PGN
            </button>
          </>
        )}

        {loadError && <p className="text-[11px] text-red-400 mt-1">{loadError}</p>}
      </div>

      {/* Undo / Redo */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={localUndo}
          disabled={!canUndo}
          className="btn-secondary flex-1 text-xs py-1.5 disabled:opacity-40"
          title="Undo last move"
        >
          ← Undo
        </button>
        <button
          type="button"
          onClick={localRedo}
          disabled={!canRedo}
          className="btn-secondary flex-1 text-xs py-1.5 disabled:opacity-40"
          title="Redo next move"
        >
          Redo →
        </button>
      </div>

      {/* Clickable move list */}
      {allMoves.length > 0 && (
        <div className="overflow-y-auto max-h-40 rounded border border-gray-800">
          <table className="w-full text-xs">
            <tbody>
              {/* Pair rows */}
              {Array.from({ length: Math.ceil(allMoves.length / 2) }, (_, pi) => {
                const wi = pi * 2;      // white half-move index (0-based)
                const bi = pi * 2 + 1; // black half-move index
                const wMove = allMoves[wi];
                const bMove = allMoves[bi];
                // cursor k means moves[0..k-1] applied, so move at idx i is active when cursor == i+1
                const wActive = cursor === wi + 1;
                const bActive = cursor === bi + 1;
                return (
                  <tr key={pi} className="border-b border-gray-800/40 last:border-0 hover:bg-gray-800/20">
                    <td className="pl-2 pr-1 py-1 text-gray-600 font-mono w-6 select-none">{pi + 1}.</td>
                    <td className="pr-0.5 py-0.5 w-1/2">
                      <button
                        ref={wActive ? activeMoveRef : undefined}
                        type="button"
                        onClick={() => jumpToMove(wi + 1)}
                        className={`w-full text-left px-1.5 py-0.5 rounded transition-colors font-mono ${
                          wActive
                            ? 'bg-blue-900/50 text-blue-300 font-semibold'
                            : wi < cursor
                            ? 'text-gray-300 hover:bg-gray-700/40'
                            : 'text-gray-600 hover:bg-gray-700/40'
                        }`}
                      >
                        {wMove.san}
                      </button>
                    </td>
                    <td className="pr-1 py-0.5 w-1/2">
                      {bMove && (
                        <button
                          ref={bActive ? activeMoveRef : undefined}
                          type="button"
                          onClick={() => jumpToMove(bi + 1)}
                          className={`w-full text-left px-1.5 py-0.5 rounded transition-colors font-mono ${
                            bActive
                              ? 'bg-blue-900/50 text-blue-300 font-semibold'
                              : bi < cursor
                              ? 'text-gray-300 hover:bg-gray-700/40'
                              : 'text-gray-600 hover:bg-gray-700/40'
                          }`}
                        >
                          {bMove.san}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Export — PGN + FEN */}
      <div>
        <p className="text-xs text-gray-500 mb-1.5">Export</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleCopyPgn}
            disabled={moves.length === 0}
            className="btn-secondary flex-1 text-xs py-1.5 disabled:opacity-40"
            title="Copy move list as PGN"
          >
            {pgnCopied ? '✓ PGN copied!' : 'Copy PGN'}
          </button>
          <button
            type="button"
            onClick={handleCopyFen}
            className="btn-secondary flex-1 text-xs py-1.5"
            title="Copy current position as FEN"
          >
            {fenCopied ? '✓ FEN copied!' : 'Copy FEN'}
          </button>
        </div>
      </div>

      {/* Analysis panel */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
            Engine Analysis
          </p>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Lines:</span>
            <div className="flex gap-0.5">
              {([1, 2, 3, 4, 5] as const).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setAnalysisLines(n)}
                  className={`w-5 h-5 text-[10px] rounded transition-colors ${
                    analysisLines === n
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Lines list */}
        <div className="space-y-1.5">
          {analysisQuery.isFetching && (
            <p className="text-xs text-gray-500 animate-pulse">Analysing…</p>
          )}
          {!analysisQuery.isFetching && analysisQuery.isError && (
            <div className="flex items-center gap-2">
              <p className="text-xs text-red-400">Analysis failed</p>
              <button
                type="button"
                onClick={() => analysisQuery.refetch()}
                className="text-xs text-blue-400 hover:text-blue-300 underline"
              >
                Retry
              </button>
            </div>
          )}
          {analysisQuery.data?.lines.map((line, i) => (
            <div
              key={i}
              className="flex items-center gap-2 py-1 px-2 rounded bg-gray-800/60 hover:bg-gray-800 transition-colors"
            >
              {/* Color indicator matching the board arrow */}
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: LINE_COLORS[i] }}
              />
              <span className="font-mono text-sm text-white flex-1">{line.san}</span>
              <span
                className={`text-xs font-mono shrink-0 ${
                  line.mate !== null
                    ? 'text-yellow-400'
                    : line.score > 50
                    ? 'text-green-400'
                    : line.score < -50
                    ? 'text-red-400'
                    : 'text-gray-400'
                }`}
              >
                {formatScore(line.score, line.mate)}
              </span>
            </div>
          ))}
          {!analysisQuery.isFetching && !analysisQuery.data?.lines.length && (
            <p className="text-xs text-gray-600 italic">No analysis yet</p>
          )}
        </div>
      </div>

      {/* Legend */}
      {(analysisQuery.data?.lines.length ?? 0) > 0 && (
        <p className="text-[10px] text-gray-600">
          Coloured arrows on the board show the top {analysisQuery.data!.lines.length} candidate move{analysisQuery.data!.lines.length > 1 ? 's' : ''}.
        </p>
      )}
    </div>
  );
}
